// PrintTemplateActions — split print/download trigger.
//
// Per PRINT_TEMPLATE_PLAN.md §6.4 + Phase 8: replaces the legacy
// `<PrintButton onPrint={() => window.print()} />` pattern on every
// page that owns a print template. Renders:
//   - a primary "Print" button (HTML mode → injects into a hidden
//     iframe + calls iframe.contentWindow.print()), and
//   - a chevron-trigger Popover menu offering both "Print" and
//     "Download PDF" so the user can opt for the binary path.
//
// The render call goes through `useRenderPrintTemplate` (see
// hooks/usePrintTemplates.ts), so the apiClient envelope-bypass
// happens for us. PDF mode uses URL.createObjectURL + a
// programmatic <a download=…> click; the URL is revoked after the
// click fires. Print mode appends a hidden iframe to document.body,
// waits for `load`, calls `print()`, and removes the iframe on
// `afterprint` (with a 60-second safety-net timeout).
//
// "Template not configured" disabled state: when usePrintTemplate
// returns an error (e.g. unknown templateType, network blip on the
// initial query) the button shows as disabled with a Tooltip so
// staff can't trigger a render that would 404 server-side.

import { useState } from 'react';
import { ChevronDown, Download, Printer } from 'lucide-react';

import { Button } from '../Button/Button';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup';
import { Popover } from '../Popover/Popover';
import { Tooltip } from '../Tooltip/Tooltip';
import {
  usePrintTemplate,
  useRenderPrintTemplate,
} from '../../../pages/settings/print-templates/hooks/usePrintTemplates';
import { useToast } from '../../../hooks/useToast';
import type { PrintTemplateType } from '../../../types/api';
import styles from './PrintTemplateActions.module.css';

const PRINT_IFRAME_CLEANUP_MS = 60_000;

export interface PrintTemplateActionsProps {
  templateType: PrintTemplateType;
  /**
   * Builds the data context to send to the renderer. Called lazily
   * — only when the user actually triggers a render — so reading
   * fresh state (e.g. the diary week object) is safe.
   */
  buildContext: () => Record<string, unknown>;
  /** Filename for the downloaded PDF. Server filename is ignored. */
  filename: string;
}

export function PrintTemplateActions({
  templateType,
  buildContext,
  filename,
}: PrintTemplateActionsProps): JSX.Element {
  const detail = usePrintTemplate(templateType);
  const render = useRenderPrintTemplate();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);

  const isLoading = render.isPending;
  // Treat any query failure as "not configured" — the user gets a
  // disabled button rather than a runtime 404 from the render call.
  const noTemplate = detail.isError;

  function fireRender(format: 'html' | 'pdf') {
    setMenuOpen(false);
    let context: Record<string, unknown>;
    try {
      context = buildContext();
    } catch (err) {
      toast.error(
        `Could not gather print data: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
      return;
    }
    render.mutate(
      { type: templateType, context, format },
      {
        onSuccess: (out) => {
          if (format === 'html') {
            if (out.html === null) {
              toast.error('Print failed: empty HTML response.');
              return;
            }
            openPrintIframe(out.html);
          } else {
            if (out.blob === null) {
              toast.error('Download failed: empty PDF response.');
              return;
            }
            triggerDownload(out.blob, filename);
          }
        },
        onError: () => {
          toast.error(
            format === 'html'
              ? 'Could not generate print preview.'
              : 'Could not generate PDF.',
          );
        },
      },
    );
  }

  if (noTemplate) {
    return (
      <Tooltip content="Template not configured">
        <Button variant="secondary" icon={Printer} disabled>
          Print
        </Button>
      </Tooltip>
    );
  }

  return (
    <ButtonGroup ariaLabel="Print actions">
      <Button
        variant="secondary"
        icon={Printer}
        onClick={() => fireRender('html')}
        loading={isLoading}
        disabled={isLoading}
      >
        Print
      </Button>
      <Popover
        trigger={
          <Button
            variant="secondary"
            icon={ChevronDown}
            disabled={isLoading}
            aria-label="More print options"
          />
        }
        open={menuOpen}
        onOpenChange={setMenuOpen}
        ariaLabel="Print options menu"
        placement="bottom"
      >
        <ul className={styles.menu} role="menu">
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => fireRender('html')}
              disabled={isLoading}
            >
              <Printer size={14} aria-hidden />
              <span>Print</span>
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => fireRender('pdf')}
              disabled={isLoading}
            >
              <Download size={14} aria-hidden />
              <span>Download PDF</span>
            </button>
          </li>
        </ul>
      </Popover>
    </ButtonGroup>
  );
}

/**
 * Append a hidden iframe to document.body, set its srcdoc, and call
 * `contentWindow.print()` once the iframe loads. The iframe is
 * removed on `afterprint` (Chromium / Firefox / WebKit all support
 * this) with a 60-second safety-net timeout in case the user
 * navigates away mid-print.
 */
function openPrintIframe(html: string): void {
  if (typeof document === 'undefined') return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('title', 'Print preview');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.srcdoc = html;
  let cleaned = false;
  function cleanup() {
    if (cleaned) return;
    cleaned = true;
    if (iframe.parentNode !== null) {
      iframe.parentNode.removeChild(iframe);
    }
  }
  iframe.addEventListener('load', () => {
    const win = iframe.contentWindow;
    if (win === null) {
      cleanup();
      return;
    }
    try {
      win.addEventListener('afterprint', cleanup);
    } catch {
      // Some browsers throw when accessing a cross-origin contentWindow
      // — srcdoc is same-origin so this branch is rare. Fall through to
      // the timeout-based cleanup.
    }
    setTimeout(cleanup, PRINT_IFRAME_CLEANUP_MS);
    try {
      win.focus();
      win.print();
    } catch {
      cleanup();
    }
  });
  document.body.appendChild(iframe);
}

/**
 * Trigger a browser download of the supplied blob via a programmatic
 * `<a download=…>` click. The object URL is revoked after the click
 * dispatches so we don't leak memory.
 */
function triggerDownload(blob: Blob, filename: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after the click has had a chance to start the download.
  // Chrome/Firefox both initiate the download synchronously on click,
  // but a microtask delay is the documented safe-defer.
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
